/**
 * ICS Parser Utility
 * Parses ICS calendar files and extracts lesson data
 */

/**
 * Parse ICS file content and extract lesson events
 * @param {string} icsContent - Raw ICS file content
 * @returns {Array} Array of parsed lesson objects
 */
export function parseICS(icsContent) {
  const lessons = [];
  const lines = icsContent.split('\n');

  let currentEvent = null;
  let inEvent = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Start of event
    if (line === 'BEGIN:VEVENT') {
      inEvent = true;
      currentEvent = {};
      continue;
    }

    // End of event
    if (line === 'END:VEVENT') {
      if (currentEvent && isValidLesson(currentEvent)) {
        const lesson = convertEventToLesson(currentEvent);
        if (lesson) {
          lessons.push(lesson);
        }
      }
      inEvent = false;
      currentEvent = null;
      continue;
    }

    // Parse event fields
    if (inEvent && currentEvent) {
      // Handle multi-line values (lines starting with space)
      if (line.startsWith(' ') && i > 0) {
        const prevLine = lines[i - 1].trim();
        const field = prevLine.split(':')[0];
        if (currentEvent[field]) {
          currentEvent[field] += line.trim();
        }
        continue;
      }

      // DTSTART
      if (line.startsWith('DTSTART')) {
        const value = line.split(':')[1];
        currentEvent.DTSTART = value;
      }

      // DTEND
      if (line.startsWith('DTEND')) {
        const value = line.split(':')[1];
        currentEvent.DTEND = value;
      }

      // SUMMARY
      if (line.startsWith('SUMMARY:')) {
        currentEvent.SUMMARY = line.substring(8);
      }

      // LOCATION
      if (line.startsWith('LOCATION:')) {
        currentEvent.LOCATION = line.substring(9);
      }

      // UID
      if (line.startsWith('UID:')) {
        currentEvent.UID = line.substring(4);
      }
    }
  }

  return lessons;
}

/**
 * Check if event is a valid lesson (has student name, not admin event)
 * @param {Object} event - Event object
 * @returns {boolean}
 */
function isValidLesson(event) {
  if (!event.SUMMARY || !event.DTSTART) return false;

  const summary = event.SUMMARY;

  // Skip admin events
  const skipPatterns = [
    'CLOSED',
    'TRIPLE THREAT - NO LESSONS',
    'Birthday',
    'Recital Day',
    'MAKEUP SESSION',
    'Singing Intensive',
    'Triple Threat'
  ];

  for (const pattern of skipPatterns) {
    if (summary.includes(pattern)) return false;
  }

  // Check if we can extract a student name
  // Use a quick check for name pattern: starts with capital letter and has an opening parenthesis
  // This is more lenient than the extraction regex to avoid rejecting valid lessons
  return /^[A-Z].*\(/.test(summary);
}

/**
 * Convert ICS event to Firestore lesson format
 * @param {Object} event - Parsed ICS event
 * @returns {Object|null} Lesson object or null if invalid
 */
function convertEventToLesson(event) {
  try {
    // Extract student name from SUMMARY
    const studentName = extractStudentName(event.SUMMARY);
    if (!studentName) return null;

    // Parse date and time from DTSTART
    const { date, time } = parseDateTimeUTC(event.DTSTART);
    if (!date || !time) return null;

    // Determine location
    const location = determineLocation(event);

    return {
      studentName,
      date,
      time,
      location,
      importedFrom: 'MMS',
      importedAt: new Date().toISOString(),
      mmsUID: event.UID || null
    };
  } catch (error) {
    console.error('Error converting event to lesson:', error, event);
    return null;
  }
}

/**
 * Extract student name from SUMMARY field
 * @param {string} summary - Event summary
 * @returns {string|null} Student name or null
 */
function extractStudentName(summary) {
  // Format: "FirstName LastName (SINGLE LESSON...)" or "FirstName LastName (Private Lesson...)"
  // More flexible regex to handle various name formats:
  // - Hyphenated names (e.g., "Mary-Anne")
  // - Apostrophes (e.g., "O'Brien")
  // - Multiple capitals (e.g., "McDonald")
  // - Short names (e.g., "Jo")
  // - Middle initials (e.g., "John A.")
  const match = summary.match(/^([A-Z][a-z]*(?:[-'][A-Z][a-z]*)*(?:\s+[A-Z]\.?)?(?:\s+[A-Z][a-z]*(?:[-'][A-Z][a-z]*)*)+)\s*\(/);

  if (match && match[1]) {
    const name = match[1].trim();
    // Validate: must have at least one space (first and last name)
    if (name.includes(' ')) {
      return name;
    }
  }

  // Fallback: try simpler pattern for basic "First Last" format
  const simpleMatch = summary.match(/^([A-Z][a-zA-Z'-]+(?:\s+[A-Z]\.?)?(?:\s+[A-Z][a-zA-Z'-]+)+)\s*\(/);
  return simpleMatch ? simpleMatch[1].trim() : null;
}

/**
 * Parse UTC datetime string and convert to local date/time
 * @param {string} dtstart - DTSTART value (e.g., "20250721T224500Z")
 * @returns {Object} { date: "YYYY-MM-DD", time: "HH:MM" }
 */
function parseDateTimeUTC(dtstart) {
  try {
    // Remove "VALUE=DATE:" prefix if present
    const cleanDtstart = dtstart.replace(/^VALUE=DATE:/, '');

    // Format: 20250721T224500Z (UTC)
    // or: 20250721 (date only)

    if (cleanDtstart.length === 8) {
      // Date only format: 20250721
      const year = cleanDtstart.substring(0, 4);
      const month = cleanDtstart.substring(4, 6);
      const day = cleanDtstart.substring(6, 8);
      return {
        date: `${year}-${month}-${day}`,
        time: '09:00' // Default time for all-day events
      };
    }

    // Full datetime format: 20250721T224500Z
    const year = cleanDtstart.substring(0, 4);
    const month = cleanDtstart.substring(4, 6);
    const day = cleanDtstart.substring(6, 8);
    const hour = cleanDtstart.substring(9, 11);
    const minute = cleanDtstart.substring(11, 13);

    // Convert UTC to Date object
    const utcDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:00Z`);

    // Convert to local time (Central Time)
    const localYear = utcDate.getFullYear();
    const localMonth = String(utcDate.getMonth() + 1).padStart(2, '0');
    const localDay = String(utcDate.getDate()).padStart(2, '0');
    const localHour = String(utcDate.getHours()).padStart(2, '0');
    const localMinute = String(utcDate.getMinutes()).padStart(2, '0');

    return {
      date: `${localYear}-${localMonth}-${localDay}`,
      time: `${localHour}:${localMinute}`
    };
  } catch (error) {
    console.error('Error parsing datetime:', error, dtstart);
    return { date: null, time: null };
  }
}

/**
 * Determine lesson location from event data
 * @param {Object} event - ICS event object
 * @returns {string} "Lakeville" or "Headline"
 */
function determineLocation(event) {
  const summary = event.SUMMARY || '';
  const location = event.LOCATION || '';

  // Check summary for explicit location
  if (summary.includes('| Lakeville')) return 'Lakeville';
  if (summary.includes('| HLDC')) return 'Headline';

  // Check LOCATION field
  if (location.includes('Lakeville')) return 'Lakeville';
  if (location.includes('BURNSVILLE')) return 'Headline';

  // Default to Headline if unclear
  return 'Headline';
}

/**
 * Filter lessons by date range
 * @param {Array} lessons - Array of lesson objects
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Array} Filtered lessons
 */
export function filterLessonsByDateRange(lessons, startDate, endDate) {
  return lessons.filter(lesson => {
    return lesson.date >= startDate && lesson.date <= endDate;
  });
}

/**
 * Group lessons by student
 * @param {Array} lessons - Array of lesson objects
 * @returns {Object} Lessons grouped by student name
 */
export function groupLessonsByStudent(lessons) {
  const grouped = {};

  lessons.forEach(lesson => {
    // Only group lessons with valid student names
    if (lesson.studentName && typeof lesson.studentName === 'string' && lesson.studentName.trim()) {
      const studentName = lesson.studentName.trim();
      if (!grouped[studentName]) {
        grouped[studentName] = [];
      }
      grouped[studentName].push(lesson);
    } else {
      console.warn('Lesson missing valid studentName:', lesson);
    }
  });

  // Sort each student's lessons by date
  Object.keys(grouped).forEach(studentName => {
    grouped[studentName].sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.time.localeCompare(b.time);
    });
  });

  return grouped;
}
